
import { useUserContext } from "../../../../contexts/user-context/UserContext";
import { useQueryNotifications } from "../../../../queries/UseQueryNotifications";
import { useGroup } from "../../../../queries/groups/UseGroup";
import { useAlarmSetpointNotificationCounts } from "../../../../queries/alarm-data/UseAlarmSetpointNotificationCounts";
import { useEffect, useMemo, useRef, useState } from "react";
import { GraphOptions } from "../../../../services/trending-data-service/Models";
import NoData from "../../../no-data/NoData";
import { useTranslation } from "react-i18next";
import Graph, { GraphType } from "../../../graph/Graph";
import { DashboardContainer, DashboardContainerType } from "../../../dashboard/dashboard-container/DashboardContainer";
import { AlarmNotificationsType, IAlarmNotificationObjectCountItemModel } from "../../../../services/alarm-data-service/Models";
import _ from "lodash";
import { publish, subscribe, unsubscribe } from "pubsub-js";
import NotificationsCountChartUtils, { NOTIFICATION_CHART_MOUSE_EVENT, NotificationCountActionItem, notificationCountActionItems } from "./NotificationsCountChartUtils";
import { NotificationsCountLegend } from "./NotificationsCountLegend";
import AlarmDashboardDetailDialog, { bindAlarmDashboardDetailDialog, useAlarmDashboardDetailDialogState } from "../alarm-dashboard-detail-dialog/AlarmDashboardDetailDialog";

import { CustomDurationDialogValues } from "../../../custom-duration-dialog/CustomDurationDialog";
import { IeLynxObject } from "../../../../common/models/eLynxObject";
import { IDashboardComponent } from "../../../../services/dashboard-data-service/Models";
import { ActionMenuItem } from "../../../action-menu/ActionMenuItem";

export type NotificationsCountChartProps = {
    notificationsType: AlarmNotificationsType,
    groupID: number,
    durationOptions: CustomDurationDialogValues,
    showEmail: boolean,
    showVoice: boolean,
    showText: boolean,

    designMode?: boolean;
    //onDrop?: (target: IDashboardComponent, component: IDashboardComponent) => void;
    onExecuteAction?: (action: ActionMenuItem, payload: IDashboardComponent) => void;

};

export type NotificationChartMouseEvent = {
    objectName: string,
    hightlight: boolean
};

export function NotificationsCountChart({
    notificationsType,
    groupID,
    durationOptions,
    showEmail,
    showVoice,
    showText,
}: NotificationsCountChartProps) {

    const [chartOptions, setChartOptions] = useState<GraphOptions | undefined>();
    const [legendData, setLegendData] = useState<IAlarmNotificationObjectCountItemModel[] | undefined>();
    const [userState] = useUserContext();
    const { t } = useTranslation();

    const graphRef = useRef<GraphType>(undefined);

    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID
    });

    const {data, status } = useQueryNotifications(useAlarmSetpointNotificationCounts)({
        type: notificationsType,
        organizationID: group?.organizationID ?? -1,
        contextGroupID: groupID,
        startDate: durationOptions.duration.startDate as Date,
        endDate: durationOptions.duration.endDate as Date,
    });

    const alarmDashboardDetailDialog = useAlarmDashboardDetailDialogState();    

    const TOPIC = useMemo(() => {
        return `${NOTIFICATION_CHART_MOUSE_EVENT}:${notificationsType}`;
    }, [
        notificationsType
    ]);    

    const title = useMemo(() => {

        return t(notificationsType === AlarmNotificationsType.Setpoints ? 
                    'alarmDashboard.top5Setpoints' :
                    notificationsType === AlarmNotificationsType.Objects ?
                    'alarmDashboard.top5Objects' :
                    'alarmDashboard.top5Recipients');

    }, [
        notificationsType,
        t
    ]);


    const drillDownToDetail = useMemo(() => async (targetObject?: IeLynxObject) => {

        await alarmDashboardDetailDialog.open({
            groupID: groupID,
            detailType: notificationsType,
            durationOptions: durationOptions,
            showEmail,
            showText,
            showVoice,
            targetObject: targetObject
        });


    },[
        alarmDashboardDetailDialog,
        groupID,
        notificationsType,
        durationOptions,
        showEmail,
        showText,
        showVoice
    ]);

    async function onExecuteAction(_actionItem: NotificationCountActionItem) {
        drillDownToDetail();
    }

    useEffect(() => {

        const onMoreInfo = (objectName: string) => {
            const target = data?.objectCountItems.find(o => o.eLynxObject.displayText === objectName)?.eLynxObject;
            drillDownToDetail(target);
        }

        const onMouseOver = (objectName: string) => {

            publish(TOPIC, {
                objectName,
                hightlight: true
            } as NotificationChartMouseEvent);
        }

        const onMouseOut = (objectName: string) => {

            publish(TOPIC, {
                objectName,
                hightlight: false
            } as NotificationChartMouseEvent);
        }

        if (data) {

            const chartData = NotificationsCountChartUtils.getChartOptions(
                            notificationsType,
                            data, 
                            showText,
                            showEmail, 
                            showVoice, 
                            onMoreInfo,
                            onMouseOver,
                            onMouseOut);
            setChartOptions({ 
                optionItems: [chartData.options] 
            });

            setLegendData(chartData.legendData);
            
        }
    }, [
        data, 
        setChartOptions,
        setLegendData,
        showText,
        showEmail,
        showVoice,
        TOPIC,
        notificationsType,
        //onExecuteAction
        drillDownToDetail
    ]);

    useEffect(() => {

        const onEvent = (chartEvent: NotificationChartMouseEvent) => {

            if (legendData) {
                const newLegendData = _.cloneDeep(legendData);

                newLegendData.forEach(o => o.highlight = false);

                // @@ was using objecttype, may need something else here
                const item = newLegendData.find(o => o.eLynxObject.displayText === chartEvent.objectName);
    
                if (item) {
                    item.highlight = chartEvent.hightlight
                }
    
                setLegendData(newLegendData);
            }
        }

        const token = subscribe(TOPIC, (message, data) => onEvent(data as NotificationChartMouseEvent));

        return () => {
            if (token) {
                unsubscribe(token);
            }
        }
    }, [
        legendData,
        setLegendData,
        TOPIC,
        notificationsType
    ]);


    // this effect should run on every render to ensure the chart fills the view
    useEffect(() => {
        // must give time for the sidesheet transition to close has completed
        setTimeout(() => {
            graphRef.current?.reflow();
        }, 500);
    });

    return (

        <DashboardContainer 
            dashboardContainerType={DashboardContainerType.Chart}
            title={title}
            loading={status === 'loading'}
            actions={notificationCountActionItems}
            onExecuteAction={(menuItem) => {onExecuteAction(menuItem as NotificationCountActionItem)}}
            >

            {(data?.objectCountItems?.length ?? 0) === 0 ?
                <NoData text={t('alarmDashboard.noData')} />
                :
                chartOptions && 
                    <>
                    <Graph ref={graphRef} options={chartOptions} />

                    {
                        (legendData?.length ?? 0) > 0 &&
                        <NotificationsCountLegend 
                            notificationsType={notificationsType} 
                            data={legendData}
                            onItemClicked={(item) => drillDownToDetail(item.eLynxObject)} />
                    }
                    </>
            }


            <AlarmDashboardDetailDialog
                {...bindAlarmDashboardDetailDialog(alarmDashboardDetailDialog)}
            />

        </DashboardContainer>
    );
}
